/** @format */

import {
  Building2,
  List,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import Header from "../../components/ui/Header";
import "../../components/ui/Header.css";
import Toast from "../../components/ui/Toast";
import "../../components/ui/Toast.css";
import Modal from "../../components/ui/Modal";
import OrgFeatureFlagsEditor from "../../components/org/OrgFeatureFlagsEditor";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { useToast } from "../../hooks/useToast";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import type { OrgRole } from "../../types/organization";
import { track } from "../../lib/posthog";
import {
  AUTH_CONSTANTS,
  isValidEmail,
  trimAuthInput,
} from "../../utils/authValidation";

type OrgSettingsTab = "organization" | "members";

const ROLE_OPTIONS: OrgRole[] = ["viewer", "editor", "admin"];

const getMemberDisplayName = (m: {
  givenName?: string | null;
  familyName?: string | null;
  email?: string;
  userId: string;
}): string => {
  const parts = [m.givenName, m.familyName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (m.email) return m.email;
  return `${m.userId.slice(0, 8)}...`;
};

const getMemberInitials = (m: {
  givenName?: string | null;
  familyName?: string | null;
  email?: string;
}): string => {
  if (m.givenName && m.familyName) {
    return `${m.givenName[0]}${m.familyName[0]}`.toUpperCase();
  }
  if (m.givenName) return m.givenName.slice(0, 2).toUpperCase();
  if (m.email) return m.email.slice(0, 2).toUpperCase();
  return "?";
};

const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as {
    response?: { data?: { message?: string; error?: string } };
  };
  return (
    axiosErr?.response?.data?.message ??
    axiosErr?.response?.data?.error ??
    (err instanceof Error ? err.message : fallback)
  );
};

interface OrganizationSettingsProps {
  /** When true, render without Header (e.g. inside Profile layout). */
  embedded?: boolean;
}

export const OrganizationSettings: FC<OrganizationSettingsProps> = ({
  embedded = false,
}) => {
  const {
    organizations,
    activeOrgId,
    members,
    isLoading,
    error,
    fetchMembers,
    addMember,
    removeMember,
    updateMemberRole,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    setActiveOrg,
    clearError,
  } = useOrganizationStore();
  const { isSuperAdmin } = useSuperAdmin();
  const { canManageMembers } = useOrgRole();
  const { toast, showToast, hideToast } = useToast();

  const [activeTab, setActiveTab] = useState<OrgSettingsTab>("organization");
  const [orgListTab, setOrgListTab] = useState<"regular" | "demo">("regular");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState<OrgRole>("editor");
  const [isInviting, setIsInviting] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createFrontendBaseUrl, setCreateFrontendBaseUrl] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<{
    orgId: string;
    name: string;
  } | null>(null);
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editFrontendBaseUrl, setEditFrontendBaseUrl] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const activeOrg = organizations.find((o) => o.orgId === activeOrgId);

  useEffect(() => {
    if (activeOrg) {
      setEditName(activeOrg.name);
      setEditSlug(activeOrg.slug);
      setEditFrontendBaseUrl(activeOrg.frontendBaseUrl ?? "");
    }
  }, [activeOrg]);

  useEffect(() => {
    if (activeOrgId && canManageMembers) {
      fetchMembers();
    }
  }, [activeOrgId, canManageMembers, fetchMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteEmailError(null);
    const trimmedEmail = trimAuthInput(inviteEmail);
    if (!trimmedEmail) {
      setInviteEmailError("Please enter an email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setInviteEmailError(
        "Please enter a valid email address (e.g., name@example.com).",
      );
      return;
    }
    setIsInviting(true);
    try {
      await addMember({
        email: trimmedEmail,
        role: inviteRole,
        sendInviteEmail: true,
      });
      track("user_invited", {
        invite_type: "org_member",
        role: inviteRole,
      });
      showToast(
        "Success",
        `${trimmedEmail} has been invited successfully.`,
        "success",
      );
      setInviteEmail("");
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to add member");
      showToast("Error", msg, "error");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMemberClick = (member: {
    userId: string;
    givenName?: string | null;
    familyName?: string | null;
    email?: string;
  }) => {
    setMemberToRemove({
      userId: member.userId,
      displayName: getMemberDisplayName(member),
    });
  };

  const handleRemoveMemberConfirm = async () => {
    if (!memberToRemove) return;
    setIsRemoving(true);
    try {
      await removeMember(memberToRemove.userId);
      showToast("Success", "Member removed.", "success");
      setMemberToRemove(null);
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to remove member");
      showToast("Error", msg, "error");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRoleChange = async (userId: string, role: OrgRole) => {
    try {
      await updateMemberRole(userId, role);
      showToast("Success", "Role updated.", "success");
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to update role");
      showToast("Error", msg, "error");
    }
  };

  const handleDeleteOrgConfirm = async () => {
    if (!orgToDelete) return;
    setIsDeletingOrg(true);
    try {
      await deleteOrganization(orgToDelete.orgId);
      showToast("Success", "Organization deleted.", "success");
      setOrgToDelete(null);
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to delete organization");
      showToast("Error", msg, "error");
    } finally {
      setIsDeletingOrg(false);
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setIsCreating(true);
    try {
      await createOrganization({
        name: createName.trim(),
        slug: createSlug.trim() || undefined,
        frontendBaseUrl: createFrontendBaseUrl.trim() || undefined,
      });
      showToast("Success", "Organization created.", "success");
      setCreateName("");
      setCreateSlug("");
      setCreateFrontendBaseUrl("");
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to create organization");
      showToast("Error", msg, "error");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId || !editName.trim()) return;
    setIsUpdating(true);
    try {
      await updateOrganization(activeOrgId, {
        name: editName.trim(),
        slug: editSlug.trim() || undefined,
        frontendBaseUrl: editFrontendBaseUrl.trim() || undefined,
      });
      showToast("Success", "Organization updated.", "success");
    } catch (err) {
      const msg = extractApiErrorMessage(err, "Failed to update organization");
      showToast("Error", msg, "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const content = (
    <div className={embedded ? "space-y-8" : "max-w-4xl mx-auto space-y-8"}>
      <div className="flex items-center gap-3">
        <Building2 className="w-10 h-10 text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">
            Organization Settings
          </h1>
          <p className="text-slate-400 text-sm">
            {activeOrg ? activeOrg.name : "Select an organization to manage"}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={clearError}
            className="text-red-400 hover:text-red-300"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="rounded-xl border border-slate-600/50 bg-slate-800/30 overflow-hidden">
        <div className="flex border-b border-slate-600/50">
          <button
            type="button"
            onClick={() => setActiveTab("organization")}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "organization"
                ? "text-blue-400 border-blue-500 bg-slate-800/50"
                : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
              }`}
          >
            <List className="w-4 h-4" />
            Organization
          </button>
          {canManageMembers && (
            <button
              type="button"
              onClick={() => setActiveTab("members")}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "members"
                  ? "text-blue-400 border-blue-500 bg-slate-800/50"
                  : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
                }`}
            >
              <Users className="w-4 h-4" />
              Members
            </button>
          )}
        </div>
        <div className="p-6 min-h-[280px] space-y-8">
          {activeTab === "organization" && (
            <div className="space-y-8">
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-200">
                      Switch organization
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">
                      {isSuperAdmin
                        ? "View and manage any organization."
                        : "Choose which organization you’re working in."}
                    </p>
                  </div>
                  {isSuperAdmin && orgListTab === "regular" && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCreateModalOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-500/50 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 text-sm"
                      >
                        <Building2 className="w-4 h-4" />
                        Create organization
                      </button>
                    </div>
                  )}
                </div>
                {/* Tab switcher — super admins only */}
                {isSuperAdmin && (
                  <div className="flex gap-1 rounded-lg border border-slate-700/60 bg-slate-900/60 p-1">
                    {(["regular", "demo"] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setOrgListTab(tab)}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          orgListTab === tab
                            ? "bg-slate-700 text-white shadow-sm"
                            : "text-slate-400 hover:text-slate-200"
                        }`}
                      >
                        {tab === "regular" ? "Organizations" : "Demo"}
                      </button>
                    ))}
                  </div>
                )}

                {/* Org list */}
                <div className="max-h-64 overflow-y-auto pr-1 space-y-2">
                  {(() => {
                    const filtered = isSuperAdmin
                      ? organizations.filter((o) =>
                          orgListTab === "demo" ? o.isDemo : !o.isDemo,
                        )
                      : organizations;
                    if (filtered.length === 0) {
                      return (
                        <div className= "flex flex-col items-center justify-center py-8 text-center rounded-lg border border-dashed border-slate-600/50 bg-slate-900/40">
                          <Building2 className="w-10 h-10 text-slate-600 mb-3" />
                          <p className="text-slate-400 text-sm">
                            {orgListTab === "demo"
                              ? "No demo organizations yet."
                              : "No organizations yet."}
                          </p>
                          {isSuperAdmin && orgListTab === "regular" && (
                            <p className="text-slate-600 text-xs mt-1">
                              Use “Create organization” to get started.
                            </p>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {filtered.map((org) => {
                          const isActive = activeOrgId === org.orgId;
                          return (
                            <div
                              key={org.orgId}
                              role="button"
                              tabIndex={0}
                              onClick={() => setActiveOrg(org.orgId)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setActiveOrg(org.orgId);
                                }
                              }}
                              className={`flex items-center gap-4 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                                isActive
                                  ? "border-blue-500/60 bg-blue-500/10"
                                  : "border-slate-600/30 bg-slate-900/50 hover:border-slate-500 hover:bg-slate-800/60"
                              }`}
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-700">
                                <Building2 className="w-5 h-5 text-slate-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-white truncate">
                                  {org.name}
                                </p>
                              </div>
                              {isSuperAdmin && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrgToDelete({
                                      orgId: org.orgId,
                                      name: org.name,
                                    });
                                  }}
                                  className="shrink-0 p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                  aria-label="Delete organization"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </section>

              {(isSuperAdmin || canManageMembers) && (
                <section className="space-y-4 border-t border-slate-600/50 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-200">
                        Organization details
                      </h2>
                      <p className="text-slate-400 text-xs mt-1">
                        Edit the name and frontend base URL.
                      </p>
                    </div>
                  </div>
                  {!activeOrg ? (
                    <p className="text-slate-500 text-sm">
                      {organizations.length === 0
                        ? "Create an organization first to manage its details."
                        : "Select an organization above to edit its details."}
                    </p>
                  ) : (
                    <form onSubmit={handleUpdateOrg} className="space-y-4 w-full">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Acme Corp"
                          required
                          className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          Frontend base URL (optional)
                        </label>
                        <input
                          type="url"
                          value={editFrontendBaseUrl}
                          onChange={(e) =>
                            isSuperAdmin
                              ? setEditFrontendBaseUrl(e.target.value)
                              : undefined
                          }
                          placeholder="https://acme.example.com"
                          disabled={!isSuperAdmin}
                          className={`w-full rounded-lg border bg-slate-800 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 ${isSuperAdmin
                              ? "border-slate-600 focus:ring-blue-500"
                              : "border-slate-700 opacity-60 cursor-not-allowed"
                            }`}
                        />
                        {!isSuperAdmin && (
                          <p className="mt-1 text-xs text-slate-500">
                            Only super admins can change the organization URL.
                          </p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isUpdating}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30 disabled:opacity-50"
                      >
                        {isUpdating && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                        Save changes
                      </button>
                    </form>
                  )}
                </section>
              )}

              {isSuperAdmin && activeOrg && (
                <section className="space-y-4 border-t border-slate-600/50 pt-6">
                  <OrgFeatureFlagsEditor
                    orgId={activeOrg.orgId}
                    initialFeatures={activeOrg.features ?? {}}
                    onSaved={() => {
                      showToast("Success", "Feature flags updated.", "success");
                    }}
                  />
                </section>
              )}
            </div>
          )}
          {activeTab === "members" && (
            <>
              {!activeOrgId ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="w-12 h-12 text-slate-600 mb-3" />
                  <p className="text-slate-500">
                    Select an organization in the Organization tab to manage
                    members.
                  </p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading members...
                </div>
              ) : (
                <div className="space-y-6">
                  <section className="space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h2 className="text-sm font-semibold text-slate-200">
                          Invite members
                        </h2>
                        <p className="text-slate-400 text-xs mt-1">
                          Invite teammates to this organization and choose their
                          role.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setInviteModalOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-500/50 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 text-sm"
                      >
                        <UserPlus className="w-4 h-4" />
                        Invite member
                      </button>
                    </div>
                  </section>

                  <section className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-200">
                        Members
                      </h2>
                      <p className="text-xs text-slate-500">
                        Change roles or remove members from this organization.
                      </p>
                    </div>
                    <div className="max-h-72 overflow-y-auto pr-1 space-y-2">
                      {members.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center rounded-lg border border-dashed border-slate-600/50 bg-slate-900/40">
                          <Users className="w-10 h-10 text-slate-600 mb-3" />
                          <p className="text-slate-400">No members yet.</p>
                          <p className="text-slate-600 text-xs mt-1">
                            Use “Invite member” above to add your first
                            teammate.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {members.map((m) => (
                            <div
                              key={m.userId}
                              className="flex items-center gap-4 rounded-lg border border-slate-600/30 bg-slate-900/50 px-4 py-3"
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-700 text-sm font-medium text-slate-200">
                                {getMemberInitials(m)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-white">
                                  {getMemberDisplayName(m)}
                                </p>
                                {m.email && (m.givenName || m.familyName) && (
                                  <p className="truncate text-xs text-slate-500">
                                    {m.email}
                                  </p>
                                )}
                              </div>
                              <select
                                value={m.role}
                                onChange={(e) =>
                                  handleRoleChange(
                                    m.userId,
                                    e.target.value as OrgRole,
                                  )
                                }
                                className="shrink-0 rounded border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                {ROLE_OPTIONS.map((r) => (
                                  <option key={r} value={r}>
                                    {r}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => handleRemoveMemberClick(m)}
                                className="shrink-0 p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                aria-label="Remove member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!activeOrgId && organizations.length > 0 && (
        <p className="text-slate-500 text-sm">
          Choose an organization in the Organization tab to manage its settings
          and members.
        </p>
      )}
    </div>
  );

  const dialogsAndToast = (
    <>
      <ConfirmDialog
        open={memberToRemove !== null}
        onClose={() => !isRemoving && setMemberToRemove(null)}
        onConfirm={handleRemoveMemberConfirm}
        title="Remove member"
        description={
          memberToRemove
            ? `Are you sure you want to remove ${memberToRemove.displayName} from the organization?`
            : ""
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isRemoving}
      />
      <ConfirmDialog
        open={orgToDelete !== null}
        onClose={() => !isDeletingOrg && setOrgToDelete(null)}
        onConfirm={handleDeleteOrgConfirm}
        title="Delete organization"
        description={
          orgToDelete
            ? `Are you sure you want to delete "${orgToDelete.name}"? This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeletingOrg}
      />
      {isSuperAdmin && (
        <Modal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title="Create organization"
          size="small"
        >
          <form
            onSubmit={(e) => {
              handleCreateOrg(e);
            }}
            className="space-y-4 w-full"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-100 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Acme Corp"
                  required
                  className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-100 mb-1">
                  Slug (optional)
                </label>
                <input
                  type="text"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  placeholder="acme-corp"
                  className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-100 mb-1">
                Frontend base URL (optional)
              </label>
              <input
                type="url"
                value={createFrontendBaseUrl}
                onChange={(e) => setCreateFrontendBaseUrl(e.target.value)}
                placeholder="https://acme.example.com"
                className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/80 border border-blue-500 text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}
      <Modal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        title="Invite member"
        size="small"
      >
        <form
          onSubmit={(e) => {
            handleInvite(e);
          }}
          className="space-y-4 w-full"
        >
          <div className="flex md:grid-cols-2 gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-100 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setInviteEmailError(null);
                  }}
                  onBlur={(e) => {
                    const trimmed = trimAuthInput(e.target.value);
                    if (trimmed !== e.target.value) {
                      setInviteEmail(trimmed);
                    }
                  }}
                  placeholder="user@example.com"
                  maxLength={AUTH_CONSTANTS.EMAIL_MAX_LENGTH}
                  required
                  className={`w-full rounded-lg border bg-slate-900 pl-10 pr-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-1 ${inviteEmailError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-slate-600 focus:ring-blue-500"
                    }`}
                />
              </div>
              {inviteEmailError && (
                <p className="mt-1 text-sm text-red-400">{inviteEmailError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-100 mb-1">
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as OrgRole)}
                className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-slate-300 text-sm">
            A email will be sent to the user to join the organization.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setInviteModalOpen(false)}
              className="px-4 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isInviting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/80 border border-blue-500 text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {isInviting && <Loader2 className="w-4 h-4 animate-spin" />}
              Invite
            </button>
          </div>
        </form>
      </Modal>
      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </>
  );

  if (embedded) {
    return (
      <>
        {content}
        {dialogsAndToast}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <main className="pt-20 px-4">{content}</main>
      {dialogsAndToast}
    </div>
  );
};
